import { FieldHint } from "@/components/field-hint/FieldHint.tsx";
import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { OrganizationListEditModal } from "@/components/organization-list/OrganizationListEditModal.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { useSystemTableConfig } from "@/hooks/use-system-table-config.hook.ts";
import i18n from "@/i18n.ts";
import { useEmployeeTerminationSave } from "@/queries/employees/use-employee-termination.query.ts";
import useEmployeeSearchInline, {
  useEmployeesByIds,
} from "@/queries/employees/use-employees.query.ts";
import { useOrganizationListByType } from "@/queries/use-organization-lists.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import {
  ApiEmployeeTerminationFull,
  ApiEmployeeTerminationPayload,
} from "@/types/employees/employee-termination.types.ts";
import {
  ApiEmployee,
  EmployeeFilterDefault,
  getEmployeeAvatarUrl,
} from "@/types/employees/employee.types.ts";
import { ApiOrganizationListType } from "@/types/organization-list.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Modal,
  ModalProps,
  Select,
  Stack,
  Text,
  Textarea,
  useModals,
} from "@skailer-com/uikit";
import { useCallback, useMemo } from "react";
import { Controller, Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ApiFieldTypes, ApiTableTypes } from "@/types/custom-field.types.ts";

export interface EmployeeTerminationEditModalProps extends ModalProps {
  employee: ApiEmployee;
  employeeTermination?: ApiEmployeeTerminationFull | null;
}

interface EmployeeTerminationFormValues {
  [key: string]: unknown;
  date_effective_from: Date | undefined;
  date_last_working: Date | null;
  termination_leave_type_id: number | undefined;
  termination_reason_id: number | undefined;
  replacement_employee_id: number | null;
  eligible_for_rehire: boolean | null;
  comment: string | null;
}

export function EmployeeTerminationEditModal({
  employee,
  employeeTermination,
  ...props
}: EmployeeTerminationEditModalProps) {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { id } = { ...props };
  const { close, openModal } = useModals();
  const currentEmployeeTermination =
    employeeTermination === undefined ? employee.employee_termination : employeeTermination;
  const resolvedTerminationId = currentEmployeeTermination?.id;
  const { fields, customFields } = useSystemTableConfig(ApiTableTypes.employee_terminations);
  const {
    getFieldByType,
    getValueForFieldFromValueObject,
    getZodSchemaForField,
    transformFormDataToValue,
  } = useCustomFieldsHook();

  const { data: terminationLeaveTypes } = useOrganizationListByType(
    ApiOrganizationListType.termination_leave_types
  );
  const { data: terminationReasons } = useOrganizationListByType(
    ApiOrganizationListType.termination_reasons
  );

  const replacementEmployeeSearch = useEmployeeSearchInline(true, {
    ...EmployeeFilterDefault,
    exclude_ids: [employee.id],
  });

  const currentTermination = useMemo((): EmployeeTerminationFormValues => {
    const terminationCustomFields =
      (currentEmployeeTermination as ApiEmployeeTerminationFull | null)?.custom_fields ?? [];
    return {
      date_effective_from: currentEmployeeTermination?.date_effective_from ?? new Date(),
      date_last_working: currentEmployeeTermination?.date_last_working ?? null,
      termination_leave_type_id:
        typeof currentEmployeeTermination?.termination_leave_type?.id === "number"
          ? currentEmployeeTermination.termination_leave_type.id
          : undefined,
      termination_reason_id:
        typeof currentEmployeeTermination?.termination_reason?.id === "number"
          ? currentEmployeeTermination.termination_reason.id
          : undefined,
      replacement_employee_id: currentEmployeeTermination?.replacement_employee?.id ?? null,
      eligible_for_rehire: currentEmployeeTermination?.eligible_for_rehire ?? null,
      comment: currentEmployeeTermination?.comment ?? null,
      ...Object.assign(
        {},
        ...customFields.map((field) => {
          const customField = terminationCustomFields.find((item) => item.field.id === field.id);
          return getValueForFieldFromValueObject(field, customField?.value, true);
        })
      ),
    };
  }, [currentEmployeeTermination, customFields, getValueForFieldFromValueObject]);

  const selectedReplacementEmployeeIds = useMemo(
    () =>
      currentTermination.replacement_employee_id
        ? [currentTermination.replacement_employee_id]
        : [],
    [currentTermination.replacement_employee_id]
  );

  const { data: selectedReplacementEmployees } = useEmployeesByIds(
    selectedReplacementEmployeeIds,
    selectedReplacementEmployeeIds.length > 0
  );

  const employeeTerminationSchema = z.object({
    termination_leave_type_id: z.number({ error: i18n.t("errors:field_required") }),
    termination_reason_id: z.number({ error: i18n.t("errors:field_required") }),
    date_effective_from: z.date({ error: i18n.t("errors:field_required") }),
    date_last_working: z.date().nullable().optional(),
    replacement_employee_id: z
      .number({ error: i18n.t("errors:field_required") })
      .nullable()
      .refine((replacementEmployeeId) => replacementEmployeeId != null, {
        error: i18n.t("errors:field_required"),
      }),
    eligible_for_rehire: z.boolean().nullable().optional(),
    comment: z.string().nullable().optional(),
    ...Object.assign({}, ...customFields.map((field) => getZodSchemaForField(field))),
  });

  const formHook = useForm<EmployeeTerminationFormValues>({
    mode: "onSubmit",
    resolver: zodResolver(
      employeeTerminationSchema
    ) as unknown as Resolver<EmployeeTerminationFormValues>,
    values: currentTermination,
  });

  const {
    control,
    register,
    getValues,
    trigger,
    formState: { errors },
  } = formHook;

  const replacementEmployeeOptions = useMemo(() => {
    const options = [...replacementEmployeeSearch.options];

    selectedReplacementEmployees?.forEach((employeeItem) => {
      if (options.some((option) => option.id === employeeItem.id)) return;

      options.unshift({
        id: employeeItem.id,
        title: renderEmployeeName(employeeItem),
        avatarUrl: getEmployeeAvatarUrl(employeeItem, "sm"),
        caption: getEmployeePositionTitle(employeeItem),
      });
    });

    return options;
  }, [replacementEmployeeSearch.options, renderEmployeeName, selectedReplacementEmployees]);

  const eligibleForRehireOptions = [
    { id: "1", title: t("common:yes") },
    { id: "0", title: t("common:no") },
  ];

  const employeeTerminationSave = useEmployeeTerminationSave(control);
  const fieldConfig = useCallback(
    (type: ApiFieldTypes) => fields.find((field) => field.type === type),
    [fields]
  );
  const fieldOrder = useCallback(
    (type: ApiFieldTypes) => fields.findIndex((field) => field.type === type),
    [fields]
  );
  const fieldTitle = (type: ApiFieldTypes, fallback: string) =>
    fieldConfig(type)?.title?.trim() || fallback;

  const handleActionButton = async () => {
    const validateResult = await trigger();
    if (!validateResult) return;

    const eligibleForRehire = getValues("eligible_for_rehire");

    const payload: ApiEmployeeTerminationPayload = {
      date_effective_from: getValues("date_effective_from") as Date,
      date_last_working: getValues("date_last_working") ?? null,
      termination_leave_type_id: getValues("termination_leave_type_id") as number,
      termination_reason_id: getValues("termination_reason_id") as number,
      replacement_employee_id: getValues("replacement_employee_id") ?? null,
      eligible_for_rehire: typeof eligibleForRehire === "boolean" ? eligibleForRehire : undefined,
      comment: getValues("comment") ?? null,
      custom_fields: Object.fromEntries(
        customFields.map((field) => [field.slug, transformFormDataToValue(field, getValues())])
      ),
    };

    try {
      await employeeTerminationSave.mutateAsync({
        employeeId: employee.id,
        terminationId: resolvedTerminationId,
        payload,
      });

      if (!resolvedTerminationId) {
        analyticsService.trackEvent(analyticEvents.employees.dismissed, {
          [analyticProperties.id]: employee.id,
        });
      }

      close(id);
    } catch {
      return;
    }
  };

  return (
    <Modal
      title={t("core:termination")}
      caption={renderEmployeeName(employee)}
      variant={"side"}
      layout={"stretch"}
      actions={
        <Stack>
          <Button
            size={"lg"}
            onClick={handleActionButton}
            isLoading={employeeTerminationSave.isPending}
          >
            {resolvedTerminationId ? t("common:save") : t("core:terminate_employee")}
          </Button>
        </Stack>
      }
      withCloser={true}
      {...props}
    >
      <Stack>
        {fieldConfig(ApiFieldTypes.effective_from) && (
          <Stack style={{ order: fieldOrder(ApiFieldTypes.effective_from) }} gap="xs">
            <Controller
              render={({ field: { value, onChange } }) => (
                <Datepicker
                  value={value}
                  onChange={onChange}
                  label={fieldTitle(ApiFieldTypes.effective_from, t("core:termination_date"))}
                  required={true}
                  error={errors.date_effective_from?.message}
                />
              )}
              control={control}
              name={"date_effective_from"}
            />
            <FieldHint hint={fieldConfig(ApiFieldTypes.effective_from)?.hint} />
          </Stack>
        )}

        {fieldConfig(ApiFieldTypes.employee_termination_leave_type) && (
          <Stack
            style={{ order: fieldOrder(ApiFieldTypes.employee_termination_leave_type) }}
            gap="xs"
          >
            <Controller
              render={({ field: { value, onChange } }) => (
                <Select
                  label={fieldTitle(
                    ApiFieldTypes.employee_termination_leave_type,
                    t("core:leave_type")
                  )}
                  options={terminationLeaveTypes?.items || []}
                  required={true}
                  emptyTitle={t("common:select")}
                  searchable
                  value={value}
                  onChange={onChange}
                  error={errors.termination_leave_type_id?.message}
                  onEditClick={() => {
                    openModal(OrganizationListEditModal, {
                      type: ApiOrganizationListType.termination_leave_types,
                    });
                  }}
                />
              )}
              control={control}
              name={"termination_leave_type_id"}
            />
            <FieldHint hint={fieldConfig(ApiFieldTypes.employee_termination_leave_type)?.hint} />
          </Stack>
        )}

        {fieldConfig(ApiFieldTypes.employee_termination_reason) && (
          <Stack style={{ order: fieldOrder(ApiFieldTypes.employee_termination_reason) }} gap="xs">
            <Controller
              render={({ field: { value, onChange } }) => (
                <Select
                  label={fieldTitle(
                    ApiFieldTypes.employee_termination_reason,
                    t("core:termination_reason")
                  )}
                  options={terminationReasons?.items || []}
                  required={true}
                  emptyTitle={t("common:select")}
                  searchable
                  value={value}
                  onChange={onChange}
                  error={errors.termination_reason_id?.message}
                  onEditClick={() => {
                    openModal(OrganizationListEditModal, {
                      type: ApiOrganizationListType.termination_reasons,
                    });
                  }}
                />
              )}
              control={control}
              name={"termination_reason_id"}
            />
            <FieldHint hint={fieldConfig(ApiFieldTypes.employee_termination_reason)?.hint} />
          </Stack>
        )}

        {fieldConfig(ApiFieldTypes.employee_termination_comment) && (
          <Stack style={{ order: fieldOrder(ApiFieldTypes.employee_termination_comment) }} gap="xs">
            <Textarea
              label={fieldTitle(ApiFieldTypes.employee_termination_comment, t("common:comments"))}
              {...register("comment")}
            />
            <FieldHint hint={fieldConfig(ApiFieldTypes.employee_termination_comment)?.hint} />
          </Stack>
        )}

        {fieldConfig(ApiFieldTypes.employee_termination_last_working_date) && (
          <Stack
            style={{ order: fieldOrder(ApiFieldTypes.employee_termination_last_working_date) }}
            gap="xs"
          >
            <Controller
              render={({ field: { value, onChange } }) => (
                <Datepicker
                  value={value}
                  onChange={onChange}
                  label={fieldTitle(
                    ApiFieldTypes.employee_termination_last_working_date,
                    t("core:last_working_day")
                  )}
                  error={errors.date_last_working?.message}
                />
              )}
              control={control}
              name={"date_last_working"}
            />
            <FieldHint
              hint={fieldConfig(ApiFieldTypes.employee_termination_last_working_date)?.hint}
            />
          </Stack>
        )}

        {fieldConfig(ApiFieldTypes.employee_termination_replacement_employee) && (
          <Stack
            style={{ order: fieldOrder(ApiFieldTypes.employee_termination_replacement_employee) }}
            gap="xs"
          >
            <Controller
              render={({ field: { value, onChange } }) => (
                <Stack gap={"xs"}>
                  <Select
                    label={fieldTitle(
                      ApiFieldTypes.employee_termination_replacement_employee,
                      t("core:termination_handover_to")
                    )}
                    required
                    emptyTitle={t("common:select")}
                    value={value}
                    searchable
                    isClearable
                    showAvatar={true}
                    showCaption={true}
                    loadOptionsOnOpen
                    isLoading={
                      replacementEmployeeSearch.isLoading ||
                      (selectedReplacementEmployeeIds.length > 0 && !selectedReplacementEmployees)
                    }
                    onSearchOptions={(text) => replacementEmployeeSearch.search(text)}
                    searchOptions={replacementEmployeeOptions}
                    onChange={onChange}
                    error={errors.replacement_employee_id?.message}
                  />
                  <Text className={"text-secondary py-2 text-sm"}>
                    {t("core:termination_handover_to_description")}
                  </Text>
                </Stack>
              )}
              control={control}
              name={"replacement_employee_id"}
            />
            <FieldHint
              hint={fieldConfig(ApiFieldTypes.employee_termination_replacement_employee)?.hint}
            />
          </Stack>
        )}

        {fieldConfig(ApiFieldTypes.employee_termination_eligible_for_rehire) && (
          <Stack
            style={{ order: fieldOrder(ApiFieldTypes.employee_termination_eligible_for_rehire) }}
            gap="xs"
          >
            <Controller
              render={({ field: { value, onChange } }) => (
                <Select
                  label={fieldTitle(
                    ApiFieldTypes.employee_termination_eligible_for_rehire,
                    t("core:eligible_for_rehire")
                  )}
                  emptyTitle={t("common:not_selected")}
                  options={eligibleForRehireOptions}
                  value={value == null ? null : value ? "1" : "0"}
                  isClearable
                  onChange={(nextValue) => {
                    if (nextValue == null) {
                      onChange(null);
                      return;
                    }
                    onChange(nextValue === "1");
                  }}
                />
              )}
              control={control}
              name={"eligible_for_rehire"}
            />
            <FieldHint
              hint={fieldConfig(ApiFieldTypes.employee_termination_eligible_for_rehire)?.hint}
            />
          </Stack>
        )}

        {customFields.map((field) => (
          <Stack key={field.id} style={{ order: fields.indexOf(field) }} gap="xs">
            {getFieldByType({
              field,
              formHook,
              canView: true,
              canEdit: true,
              label: field.title,
              options: field.options.map((option) => ({
                id: Number(option.id),
                title: option.title,
              })),
            })}
          </Stack>
        ))}
      </Stack>
    </Modal>
  );
}
